/**
 * Small controlled inputs for the server-persisted registration wizard.
 *
 * The wizard holds its per-step state in plain React state (seeded from the
 * server and saved on "Continue"), so — unlike {@link ../fields} which binds to
 * TanStack Form fields — these take a value + onChange directly.
 */
import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Modal } from "@/components/ui/modal";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils/cn";

import { wordLength } from "../../../lib/text";

type Option = { value: string; label: string };

/** Options from which on a SelectionField gets a search box. */
const SEARCH_FROM = 12;

/** Case- and accent-insensitive, so "quebec" finds "Québec". */
function normalizeForSearch(value: string) {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function TextRow({
  label,
  value,
  onChange,
  type = "text",
  className,
  required,
  error,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  className?: string;
  required?: boolean;
  error?: string;
  /** A column limit, capping the field as the Django widget's own
   *  `maxlength` caps it. No counter: these are short single-line values. */
  maxLength?: number;
}) {
  const id = useId();
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={id}>
        {required && <span className="mr-0.5 text-primary">*</span>}
        {label}
      </Label>
      <Input
        id={id}
        type={type}
        value={value}
        maxLength={maxLength}
        aria-invalid={error ? true : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

/** A required Oui/Non question rendered as a radio pair (old app pattern). */
export function YesNoRow({
  label,
  value,
  onChange,
  name,
  required,
  error,
}: {
  label: string;
  value: boolean | null | undefined;
  onChange: (value: boolean) => void;
  name: string;
  required?: boolean;
  error?: string;
}) {
  const { t } = useTranslation();
  return (
    <fieldset className="space-y-2">
      <legend className="text-sm font-medium">
        {required && <span className="mr-0.5 text-primary">*</span>}
        {label}
      </legend>
      <div className="flex gap-6">
        {[
          { v: true, label: t("expert.register.yes") },
          { v: false, label: t("expert.register.no") },
        ].map((opt) => (
          <label key={String(opt.v)} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={name}
              checked={value === opt.v}
              onChange={() => onChange(opt.v)}
              className="size-4 border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            {opt.label}
          </label>
        ))}
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
    </fieldset>
  );
}

export function NumberRow({
  label,
  value,
  onChange,
  className,
  min = 0,
  max,
  required,
  error,
}: {
  label: string;
  value: number | null;
  onChange: (value: number | null) => void;
  className?: string;
  min?: number;
  max?: number;
  required?: boolean;
  error?: string;
}) {
  const id = useId();
  return (
    <div className={cn("space-y-1.5", className)}>
      <Label htmlFor={id}>
        {required && <span className="mr-0.5 text-primary">*</span>}
        {label}
      </Label>
      <Input
        id={id}
        type="number"
        min={min}
        max={max}
        inputMode="numeric"
        value={value == null ? "" : String(value)}
        aria-invalid={error ? true : undefined}
        onChange={(e) =>
          onChange(e.target.value === "" ? null : Math.max(0, Number(e.target.value)))
        }
      />
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export function TextareaRow({
  label,
  value,
  onChange,
  rows = 3,
  className,
  required,
  error,
  wordLimit,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  className?: string;
  required?: boolean;
  error?: string;
  /** Shows a counter and flags going over, counted as Word counts (see
   *  `lib/text.ts`). No `maxLength`: it would count line breaks. */
  wordLimit?: number;
  /** A plain column limit, counted the way the browser counts: the field is
   *  capped and the counter shows how much is left. Mutually exclusive with
   *  `wordLimit`, which deliberately refuses a hard cap. */
  maxLength?: number;
}) {
  const id = useId();
  const limit = wordLimit ?? maxLength;
  const used = wordLimit ? wordLength(value) : maxLength ? value.length : 0;
  return (
    <div className={cn("space-y-1.5", className)}>
      {/* A textarea label can be a full instruction that wraps (the mandate
          description's does), and the base Label's leading-none packs its
          lines together. */}
      <Label htmlFor={id} className="leading-snug">
        {required && <span className="mr-0.5 text-primary">*</span>}
        {label}
      </Label>
      <textarea
        id={id}
        rows={rows}
        value={value}
        aria-invalid={error ? true : undefined}
        maxLength={wordLimit ? undefined : maxLength}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "flex w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          error && "border-destructive",
        )}
      />
      {limit && (
        <p
          className={cn(
            "text-right text-xs",
            used > limit ? "text-destructive" : "text-muted-foreground",
          )}
        >
          {used}/{limit}
        </p>
      )}
      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
}

export function CheckRow({
  label,
  checked,
  onChange,
  className,
  required,
}: {
  label: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  className?: string;
  required?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex min-h-touch cursor-pointer items-start gap-2 text-sm",
        className,
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-4 shrink-0 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      <span>
        {required && <span className="mr-0.5 text-primary">*</span>}
        {label}
      </span>
    </label>
  );
}

/**
 * A multi-select facet rendered as a chips summary + "Select / Edit" button that
 * opens a modal switch-list — the old app's `_switch_list.html` pattern. Keeps a
 * long option list off the main form; a "select all" master toggle and an
 * optional free-text "Autre(s)" field cover bulk selection and write-ins.
 */
export function SelectionField({
  label,
  hint,
  options,
  groups,
  selected,
  onChange,
  selectAllLabel,
  otherLabel,
  otherToggleLabel,
  otherOption,
  otherValue,
  onOtherChange,
  required,
  error,
}: {
  label: string;
  /** Optional note under the control's label, for a facet that needs one. */
  hint?: string;
  options: Option[];
  /** Optional category grouping for the modal list (chips + select-all still
   * operate over the flat `options`). Mirrors the old app's grouped switch list. */
  groups?: { heading: string; options: Option[] }[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** The Django switch list takes the same label as an optional parameter, and
   * omitting it here drops the master switch the same way. */
  selectAllLabel?: string;
  otherLabel?: string;
  /** When set, the free-text box hides behind an "Autre" switch instead of being
   * always visible. Opt-in: callers that omit it keep the permanent box. */
  otherToggleLabel?: string;
  /** Value of a real "Autre" option in the list, which then reveals the free-text
   * box itself — for a catalogue that already carries its own "Autre" choice and
   * would read oddly with a second switch beside it. Mutually exclusive with
   * `otherToggleLabel`. Mirrors `_switch_list.html`'s `other_option`. */
  otherOption?: string;
  otherValue?: string;
  onOtherChange?: (value: string) => void;
  required?: boolean;
  error?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Closing drops the filter: reopening on a stale search would show a subset of
  // the catalogue, with both levels of "select all" silently scoped to it.
  const close = () => {
    setSearch("");
    setOpen(false);
  };
  // Derived from the value, so re-opening an aid that has précisions shows the
  // switch already on.
  const [otherOn, setOtherOn] = useState(Boolean(otherValue?.trim()));
  // The free-text box needs a real label association; without it a screen reader
  // announces an unlabelled field.
  const otherInputId = useId();
  const searchInputId = useId();
  const set = new Set(selected);
  const chosen = options.filter((o) => set.has(o.value));
  const isEmpty = chosen.length === 0 && !otherValue?.trim();
  // Whether the free-text box is on screen: gated by the "Autre" option when the
  // catalogue owns one, else by the dedicated switch, else always visible.
  const otherRevealed = otherOption
    ? set.has(otherOption)
    : !otherToggleLabel || otherOn;

  // What the closed control shows: one badge for "everything", one per fully
  // picked group, and a plain chip for each remaining option — in the modal's own
  // order, so the summary reads like the list it summarizes. A badge names what it
  // collapsed in `covers`, which the markup hangs on `title`.
  const summary: { key: string; label: string; collapsed?: true; covers?: string }[] =
    (() => {
      const names = (group: Option[]) => group.map((o) => o.label).join(", ");
      if (selectAllLabel && options.length > 0 && chosen.length === options.length) {
        return [
          {
            key: "__all",
            label: selectAllLabel,
            collapsed: true as const,
            // Hovering a group badge names the options it stands for; hovering
            // this one would name all 106, which is noise — it names the groups.
            covers: groups?.length
              ? groups.map((group) => group.heading).join(", ")
              : names(options),
          },
        ];
      }
      const entries: {
        key: string;
        label: string;
        collapsed?: true;
        covers?: string;
      }[] = [];
      for (const group of groups ?? [{ heading: "", options }]) {
        const picked = group.options.filter((o) => set.has(o.value));
        if (picked.length === 0) continue;
        if (group.heading && picked.length === group.options.length) {
          entries.push({
            key: `__group-${group.heading}`,
            label: t("expert.register.allOfGroup", { name: group.heading }),
            collapsed: true as const,
            covers: names(group.options),
          });
          continue;
        }
        entries.push(...picked.map((o) => ({ key: o.value, label: o.label })));
      }
      return entries;
    })();

  // The list on screen. Every bulk switch acts on *these*: with a search active
  // the visible rows are what a "select all" appears to promise, and reaching
  // past them would silently tick options the user cannot see. The Django
  // switch list scopes its own masters the same way.
  const matches = (option: Option) =>
    !search.trim() || normalizeForSearch(option.label).includes(normalizeForSearch(search));
  const shownGroups = (groups ?? [{ heading: "", options }])
    .map((group) => ({ ...group, options: group.options.filter(matches) }))
    .filter((group) => group.options.length > 0);
  const shown = shownGroups.flatMap((group) => group.options);
  // Long enough to scan rather than read (the Django control reveals its box at
  // the same count). The 106 MRCs need it; five funding types do not.
  const searchable = options.length >= SEARCH_FROM;

  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    // Deselecting the "Autre" option takes its précision with it — a leftover
    // would come back the next time the option is picked.
    if (otherOption && value === otherOption && !next.has(value) && otherValue) {
      onOtherChange?.("");
    }
    onChange([...next]);
  };

  /** Tick or clear every option of `scope` (all of them, or one group's). */
  const bulk = (scope: Option[], on: boolean) => {
    const next = new Set(selected);
    for (const option of scope) {
      if (on) next.add(option.value);
      else next.delete(option.value);
    }
    onChange([...next]);
  };
  const stateOf = (scope: Option[]) => {
    const on = scope.filter((option) => set.has(option.value)).length;
    return { all: scope.length > 0 && on === scope.length, some: on > 0 };
  };
  const allState = stateOf(shown);

  return (
    <div>
      <div
        className={cn(
          "rounded-md border p-3",
          error ? "border-destructive" : "border-border",
        )}
      >
        <div className="flex items-center justify-between gap-3">
          <Label>
            {required && <span className="mr-0.5 text-primary">*</span>}
            {label}
            {hint && (
              <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                {hint}
              </span>
            )}
          </Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="flex-shrink-0"
            onClick={() => setOpen(true)}
          >
            {isEmpty ? t("expert.register.select") : t("expert.register.modify")}
          </Button>
        </div>
        <div data-chips className="mt-2.5 flex min-w-0 flex-wrap gap-1.5">
          {isEmpty ? (
            // Nothing chosen: a single red badge instead of a plain placeholder,
            // so an empty required facet reads as "still to do" at a glance.
            <span className="inline-flex items-center rounded-full border border-destructive/40 px-2.5 py-0.5 text-xs font-medium text-destructive">
              {t("expert.register.noneSelected")}
            </span>
          ) : (
            // A complete selection collapses to one badge rather than 106 chips:
            // this control is a one-row summary and "everything" is what there is
            // to read. Same one level down — a région whose every MRC is picked
            // reads « Tout Abitibi-Témiscamingue » — with the collapsed options
            // named on hover, and the modal still showing the real switches.
            summary.map((entry) =>
              entry.collapsed ? (
                <span
                  key={entry.key}
                  title={entry.covers}
                  className="cursor-help rounded-full bg-foreground px-2.5 py-0.5 text-xs font-semibold text-background"
                >
                  {entry.label}
                </span>
              ) : (
                <span
                  key={entry.key}
                  className="rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs"
                >
                  {entry.label}
                </span>
              ),
            )
          )}
          {/* Free text, so arbitrarily long: this closed control is a one-row
              summary, so truncate and keep the full value on hover. `isEmpty`
              already accounts for the précision, so it needs no second guard. */}
          {otherValue?.trim() && (
            <span
              title={otherValue}
              className="max-w-[18rem] truncate rounded-full border border-border bg-secondary px-2.5 py-0.5 text-xs"
            >
              {otherValue}
            </span>
          )}
        </div>
      </div>
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}

      {open && (
        <Modal title={label} onClose={close}>
          <div className="space-y-2">
            {searchable && (
              <div>
                <label htmlFor={searchInputId} className="sr-only">
                  {t("expert.register.searchIn", { name: label })}
                </label>
                <input
                  id={searchInputId}
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  // The modal renders inline, so this box sits inside the page's
                  // <form>: Enter would submit it (saving the record mid-edit)
                  // and the filter is applied on input anyway.
                  onKeyDown={(e) => {
                    if (e.key === "Enter") e.preventDefault();
                  }}
                  placeholder={t("expert.register.search")}
                  className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
            )}
            {selectAllLabel && (
              <label className="flex items-center justify-between gap-3 border-b border-border pb-3 text-sm font-semibold">
                <span>{selectAllLabel}</span>
                <Switch
                  checked={allState.all}
                  indeterminate={allState.some && !allState.all}
                  disabled={shown.length === 0}
                  onCheckedChange={(next) => bulk(shown, next)}
                  label={selectAllLabel}
                />
              </label>
            )}
            {shown.length === 0 && (
              <p className="py-2 text-sm text-muted-foreground">
                {t("expert.register.noSearchResult")}
              </p>
            )}
            {shownGroups.map((group) => {
              const groupState = stateOf(group.options);
              return (
                <div key={group.heading || "__all"}>
                  {group.heading && (
                    // The heading carries its own "select all": on a long grouped
                    // catalogue (régions → MRCs), "this whole région" is the
                    // selection an administrator actually makes.
                    <div className="mt-3 flex items-center justify-between gap-3 border-b border-border/60 pb-1">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {group.heading}
                      </p>
                      <label className="flex shrink-0 items-center gap-2 text-xs text-muted-foreground">
                        <span>{t("expert.register.selectAll")}</span>
                        <Switch
                          checked={groupState.all}
                          indeterminate={groupState.some && !groupState.all}
                          onCheckedChange={(next) => bulk(group.options, next)}
                          // Named after the group, so 17 of these don't all
                          // announce the same thing.
                          label={t("expert.register.selectAllIn", {
                            name: group.heading,
                          })}
                        />
                      </label>
                    </div>
                  )}
                  {group.options.map((o) => (
                    <label
                      key={o.value}
                      className="flex items-center justify-between gap-3 border-t border-border/60 py-2.5 text-sm first:border-t-0"
                    >
                      <span>{o.label}</span>
                      <Switch
                        checked={set.has(o.value)}
                        onCheckedChange={() => toggle(o.value)}
                        label={o.label}
                      />
                    </label>
                  ))}
                </div>
              );
            })}
            {onOtherChange && otherToggleLabel && !otherOption && (
              <label className="flex items-center justify-between gap-3 border-t border-border pt-3 text-sm font-semibold">
                <span>{otherToggleLabel}</span>
                <Switch
                  checked={otherOn}
                  onCheckedChange={(next) => {
                    setOtherOn(next);
                    // Clearing on un-toggle keeps the switch honest: its state is
                    // derived from the value, so a hidden leftover would come
                    // back checked.
                    if (!next && otherValue) onOtherChange("");
                  }}
                  label={otherToggleLabel}
                />
              </label>
            )}
            {onOtherChange && otherRevealed && (
              <div className="space-y-1.5 border-t border-border pt-3">
                <Label htmlFor={otherInputId}>{otherLabel}</Label>
                <Input
                  id={otherInputId}
                  value={otherValue ?? ""}
                  onChange={(e) => onOtherChange(e.target.value)}
                />
              </div>
            )}
            <div className="flex justify-end border-t border-border pt-3">
              <Button type="button" onClick={close}>
                {t("expert.register.done")}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * A compact scope facet: a "select all" header over a scrollable switch list, so
 * a long per-enjeu option pool stays contained instead of stacking down the
 * page. Used for the step-2 per-enjeu sectors / sizes / org-types.
 */
export function ScopeBox({
  label,
  options,
  selected,
  onChange,
  error,
}: {
  label: string;
  options: Option[];
  selected: string[];
  onChange: (next: string[]) => void;
  error?: string;
}) {
  const { t } = useTranslation();
  const set = new Set(selected);
  const allOn = options.length > 0 && selected.length === options.length;
  const toggle = (value: string) => {
    const next = new Set(selected);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange([...next]);
  };
  const toggleAll = () => onChange(allOn ? [] : options.map((o) => o.value));

  return (
    <div>
      <div
        className={cn(
          "overflow-hidden rounded-md border",
          error ? "border-destructive" : "border-border",
        )}
      >
        <div className="flex items-center justify-between gap-2 border-b border-border bg-secondary/40 px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </span>
          <Switch
            checked={allOn}
            indeterminate={selected.length > 0 && !allOn}
            onCheckedChange={toggleAll}
            label={t("expert.register.selectAll")}
          />
        </div>
        <div className="max-h-40 overflow-y-auto">
          {options.map((o) => (
            <label
              key={o.value}
              className="flex items-center justify-between gap-2 border-t border-border/60 px-3 py-2 text-sm first:border-t-0"
            >
              <span>{o.label}</span>
              <Switch
                checked={set.has(o.value)}
                onCheckedChange={() => toggle(o.value)}
                label={o.label}
              />
            </label>
          ))}
        </div>
      </div>
      {error && <p className="mt-1 text-sm text-destructive">{error}</p>}
    </div>
  );
}

export function CheckGroup({
  label,
  options,
  selected,
  onToggle,
  className,
  columns = 2,
}: {
  label: string;
  options: { value: string; label: string }[];
  selected: string[];
  onToggle: (value: string, checked: boolean) => void;
  className?: string;
  columns?: 1 | 2 | 3;
}) {
  const set = new Set(selected);
  const grid =
    columns === 3
      ? "sm:grid-cols-3"
      : columns === 1
        ? "grid-cols-1"
        : "sm:grid-cols-2";
  return (
    <fieldset className={cn("space-y-1.5", className)}>
      <legend className="text-sm font-medium">{label}</legend>
      <div className={cn("grid gap-1.5", grid)}>
        {options.map((o) => (
          <label
            key={o.value}
            className="flex min-h-touch cursor-pointer items-center gap-2 text-sm"
          >
            <input
              type="checkbox"
              checked={set.has(o.value)}
              onChange={(e) => onToggle(o.value, e.target.checked)}
              className="size-4 shrink-0 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <span>{o.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}
